import React from 'react';
import '../privacy-policy.css'; // Import a separate CSS file for styling

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-header">
        <h1>Privacy Policy</h1>
        <p>Last updated: 29th March 2025</p>
      </div>

      <p>
        At AddressKeeper, we value your privacy and are committed to protecting your personal data. This Privacy Policy outlines how we collect, use, and safeguard your data when you use our extension and services. By using our services, you agree to the collection and use of information in accordance with this policy.
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        We collect personal information such as your address, name, phone number, email, and other data you provide while using the AddressKeeper Extension. This data is necessary for the functioning of our service, such as fetching and storing addresses.
      </p>

      <h2>2. How We Use Your Information</h2>
      <p>Your personal information is used exclusively for the following purposes:</p>
      <ul>
        <li>To provide you with accurate address-related services (e.g., fetching, storing, and filling address information).</li>
        <li>To communicate with you regarding your account, service updates, and new features.</li>
        <li>For administrative purposes, including processing requests and improving our services.</li>
      </ul>

      <h2>3. Data Storage and Security</h2>
      <p>
        Your data is securely stored on third-party servers. We do not own or manage the servers where your data is stored. The following third-party services are involved in the hosting and management of your data:
      </p>
      <ul>
        <li><strong>Render.com:</strong> Used for deploying the AddressKeeper app and handling requests. Render.com provides secure cloud infrastructure and follows best practices for data security.</li>
        <li><strong>Netlify:</strong> Used for hosting the static parts of our app. Netlify is committed to maintaining security and privacy practices to ensure your data is protected.</li>
        <li><strong>Neon Database:</strong> Used for storing address-related data. Neon provides a managed PostgreSQL database, which ensures the security and integrity of your personal data. Neon follows industry-standard practices for database encryption and data safety.</li>
      </ul>
      <p>While we strive to protect your data, we cannot guarantee its absolute security due to the inherent risks associated with online data transmission. By using our service, you acknowledge these risks.</p>

      <h2>4. How This Works</h2>
      <p>To use this extension, users must follow the steps below:</p>
      <ol>
        <li>
          First, users need to register themselves and add their address information on the website: 
          <a href="https://addresskeeper.netlify.app/" target="_blank" rel="noopener noreferrer">https://addresskeeper.netlify.app/</a>.
        </li>
        <li>
          After registration, an addressCode will be generated for each address that is added.
        </li>
        <li>
          In the extension window, users can input this unique addressCode. The extension will then fetch the address information from the server.
        </li>
        <li>
          Finally, the fetched address information will be available for the user to fill automatically into the active web page (e.g., Flipkart, Amazon, etc.).
        </li>
      </ol>

      <h2>5. Data Retention</h2>
      <p>We retain your personal data for as long as necessary to fulfill the purposes outlined in this policy or as required by law. If you decide to delete your account or request the removal of your personal information, we will comply with your request in accordance with applicable laws.</p>

      <h2>6. Sharing Your Information</h2>
      <p>We do not sell, trade, or rent your personal data to third parties. However, we may share your information in the following circumstances:</p>
      <ul>
        <li><strong>Service Providers:</strong> We may share your data with trusted third-party service providers (such as Render.com, Netlify, and Neon) to facilitate the operation of our app and services. These providers are required to handle your data securely and in compliance with this Privacy Policy.</li>
        <li><strong>Legal Compliance:</strong> We may disclose your data if required by law or if we believe such action is necessary to comply with legal obligations, protect our rights, or investigate fraud.</li>
      </ul>

      <h2>7. Your Data Protection Rights</h2>
      <p>You have the right to access, modify, delete, and restrict the use of your personal data. Depending on your jurisdiction, you may have the following rights:</p>
      <ul>
        <li><strong>Access:</strong> You can request a copy of the personal data we hold about you.</li>
        <li><strong>Rectification:</strong> You can request correction of any inaccurate or incomplete personal data.</li>
        <li><strong>Deletion:</strong> You can request the deletion of your personal data when it is no longer necessary for the purposes outlined in this policy.</li>
        <li><strong>Restriction:</strong> You can request restrictions on the processing of your personal data.</li>
        <li><strong>Objection:</strong> You can object to the processing of your personal data for marketing purposes.</li>
      </ul>
      <p>To exercise these rights, please contact us using the information provided at the end of this policy.</p>

      <h2>8. Cookies and Tracking Technologies</h2>
      <p>We use cookies and similar tracking technologies to enhance your user experience and analyze usage patterns. You can control cookies through your browser settings, but disabling them may affect the functionality of the service.</p>

      <h2>9. Third-Party Links</h2>
      <p>Our extension may contain links to third-party websites or services that are not operated by us. We are not responsible for the privacy practices of these third-party sites. We encourage you to review their privacy policies before providing any personal information.</p>

      <h2>10. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with the updated date. We encourage you to periodically review this page for any updates. Your continued use of the service after any changes signifies your acceptance of the updated Privacy Policy.</p>

      <h2>11. Clarification: Project Purpose</h2>
      <p>This extension was created as part of a learning project. While it is fully functional and can be used for its intended purposes, please note that we are not a company or organization behind this service. We do not sell or monetize this project, and it is not being used for commercial purposes. Our goal is solely to learn how technologies like web extensions, APIs, and data storage systems work in real-world scenarios and how they can help solve practical problems. The extension is provided free of charge and is for educational purposes only.</p>

      <h2>12. Contact Us</h2>
      <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us at:</p>
      <p><strong>Email:</strong> <a href="mailto:addresskeeper1@gmail.com">addresskeeper1@gmail.com</a></p> 
    </div>
  );
};

export default PrivacyPolicy;
